import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/auth/authContext';
import { getUnseenAnswerCount } from '@/services/reportService';
import { onBadgesChanged } from '@/services/badgeSignal';

/** Slower than chat's 20s: an answer arrives hours after the report, not seconds. */
export const REPORT_POLL_MS = 60_000;

/**
 * The Bildir tab badge — answered reports the user has not opened yet.
 *
 * Polls, refetches on focus, and refetches on the badge signal — which fires
 * when an answer arrives and when the answers are opened, here or elsewhere.
 */
export function useUnseenReportAnswers(): number {
  const { status } = useAuth();
  const [count, setCount] = useState(0);

  const refresh = useCallback(() => {
    if (status !== 'authed') {
      setCount(0);
      return;
    }
    getUnseenAnswerCount()
      .then(setCount)
      .catch(() => {
        /* a badge is not worth surfacing an error for */
      });
  }, [status]);

  useEffect(() => {
    refresh();
    if (status !== 'authed') return;

    const timer = window.setInterval(refresh, REPORT_POLL_MS);
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    const offBadges = onBadgesChanged(refresh);

    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      offBadges();
    };
  }, [status, refresh]);

  return count;
}
